"use client"

import type { MfaMethod } from "@opensea/embedded-wallet-sdk"
import { initMfaEnrollmentWithSms } from "@opensea/embedded-wallet-sdk"
import {
  CenterAligned,
  classNames,
  Flex,
  FlexColumn,
  FormControl,
  List,
  ListItem,
  ListItemAvatar,
  ListItemContent,
  ListItemDescription,
  ListItemSide,
  ListItemTitle,
  PhoneInput,
  Spinner,
  TextBody,
  TextDisplay,
  Tooltip,
  UnstyledButton,
} from "@opensea/ui-kit"
import {
  ArrowForward,
  Check,
  PhoneAndroid,
  VerifiedUser,
} from "@opensea/ui-kit/icons"
import { QRCodeSVG } from "qrcode.react"
import { useEffect, useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { AuthCode } from "../../../components/AuthCode"
import { Button } from "../../../components/Button"
import { ContentCopy } from "../../../components/icons/ContentCopy"
import { ModalBackButton } from "../../../components/Modal"
import { PasteFromClipboardButton } from "../../../components/PasteFromClipboardButton"
import { ResendCode } from "../../../components/ResendCode"
import {
  MfaEnrollmentActions,
  MfaEnrollmentState,
} from "../../../stores/mfaEnrollmentStore"
import { useMfaMethods } from "../../use-mfa-methods"

const CODE_EXPIRATION_MS = 10 * 60 * 1000

type SelectEnrollmentOptionProps = {
  enrolledMfaMethods: MfaMethod[]
}

const SelectEnrollmentOption = ({
  enrolledMfaMethods,
}: SelectEnrollmentOptionProps) => {
  const t = useTranslations("wallet.MfaEnrollment")
  const { initializingStep } = useSnapshot(MfaEnrollmentState)
  return (
    <FlexColumn className="gap-8 pt-12 pb-6">
      <FlexColumn className="gap-4">
        <TextDisplay size="sm">{t("activateMultiFactorTitle")}</TextDisplay>
        <TextBody color="text-secondary" size="sm">
          {t("activateMultiFactorDescription")}
        </TextBody>
      </FlexColumn>
      <List>
        <Tooltip
          content={t("alreadyEnrolled")}
          disabled={!enrolledMfaMethods.includes("sms")}
        >
          <div>
            <ListItem
              disabled={enrolledMfaMethods.includes("sms")}
              onClick={async () =>
                await MfaEnrollmentActions.selectEnrollmentOption("sms")
              }
            >
              <ListItemAvatar icon={PhoneAndroid} />
              <ListItemContent>
                <ListItemTitle>{t("sms")}</ListItemTitle>
                <ListItemDescription className="text-start">
                  {t("smsDescription")}
                  <br />
                  {t("smsRegionRestriction")}
                </ListItemDescription>
              </ListItemContent>
              {initializingStep === "enroll-sms" && <Spinner />}
            </ListItem>
          </div>
        </Tooltip>
        <Tooltip
          content={t("alreadyEnrolled")}
          disabled={!enrolledMfaMethods.includes("totp")}
        >
          <div>
            <ListItem
              disabled={enrolledMfaMethods.includes("totp")}
              onClick={async () =>
                await MfaEnrollmentActions.selectEnrollmentOption("totp")
              }
            >
              <ListItemAvatar icon={VerifiedUser} />
              <ListItemContent>
                <ListItemTitle>{t("authenticator")}</ListItemTitle>
                <ListItemDescription className="text-start">
                  {t("authenticatorDescription")}
                  <br />
                  {t("authenticatorSecurityNote")}
                </ListItemDescription>
              </ListItemContent>
              <ListItemSide>
                {initializingStep === "enroll-totp" && <Spinner />}
              </ListItemSide>
            </ListItem>
          </div>
        </Tooltip>
      </List>
    </FlexColumn>
  )
}

export const EnrollSms = () => {
  const t = useTranslations("wallet.MfaEnrollment")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [intializing, setInitializing] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [code, setCode] = useState("")
  const { submissionError } = useSnapshot(MfaEnrollmentState)
  const onBack = isInitialized
    ? () => setIsInitialized(false)
    : MfaEnrollmentActions.onBack

  const onSendCode = async () => {
    setInitializing(true)
    await initMfaEnrollmentWithSms(phoneNumber)
    setIsInitialized(true)
    setInitializing(false)
  }

  const [codeExpired, setCodeExpired] = useState(false)
  const [lastSendTime, setLastSendTime] = useState<Date>(() => new Date())
  const resendCode = async () => {
    await onSendCode()
    setLastSendTime(new Date())
    MfaEnrollmentActions.setSubmissionError(undefined)
    setCode("")
  }

  // Clear error when code changes
  useEffect(() => {
    MfaEnrollmentActions.setSubmissionError(undefined)
  }, [code])

  const onSubmit = async (currentCode: string) => {
    setCode(currentCode)
    await MfaEnrollmentActions.submitEnrollment({
      code: currentCode,
      phoneNumber,
    })
  }

  return (
    <>
      <FlexColumn className="gap-8 pt-12 pb-6">
        <FlexColumn className="gap-4">
          <TextDisplay className="leading-[28px]" size="sm">
            {isInitialized ? t("enterCode") : t("enterPhoneNumber")}
          </TextDisplay>
          <TextBody className="text-center" color="text-secondary" size="sm">
            {isInitialized ? t("codeSentViaSMS") : t("codeWillBeSentViaSMS")}
          </TextBody>
        </FlexColumn>
        <CenterAligned className="gap-4">
          <PhoneInput
            className={classNames("h-14 bg-bg-primary *:bg-bg-primary")}
            disabled={isInitialized || intializing}
            onEnter={onSendCode}
            onValueChange={setPhoneNumber}
            overrides={{
              Input: {
                className: classNames("h-full overflow-hidden"),
                endEnhancer: (
                  <Button
                    className="h-8 rounded-lg p-1.5"
                    icon={
                      intializing ? (
                        <CenterAligned className="size-5">
                          <Spinner color="white" size="xs" />
                        </CenterAligned>
                      ) : (
                        ArrowForward
                      )
                    }
                    size="sm"
                    type="submit"
                  />
                ),
              },
            }}
            value={phoneNumber}
          />
          {isInitialized && (
            <TextBody className="text-center" color="text-secondary" size="sm">
              {t("checkPhoneForCode")}
            </TextBody>
          )}
        </CenterAligned>
        {isInitialized ? (
          <>
            <FormControl
              error={submissionError}
              hideLabel
              label={t("authenticationCodeLabel")}
              overrides={{
                Caption: {
                  className: "justify-center",
                },
              }}
            >
              <AuthCode
                autoFocus
                error={codeExpired || !!submissionError}
                onChange={setCode}
                onComplete={onSubmit}
                value={code}
              />
            </FormControl>
            <div>
              <PasteFromClipboardButton
                className="mt-8 md:hidden"
                onPaste={onSubmit}
              />
              <ResendCode
                codeExpiration={lastSendTime.getTime() + CODE_EXPIRATION_MS}
                sendCode={resendCode}
                setCodeExpired={setCodeExpired}
              />
            </div>
          </>
        ) : (
          <CenterAligned className="gap-4">
            <Button
              className="w-full"
              disabled={intializing}
              isLoading={intializing}
              onClick={onSendCode}
              size="lg"
            >
              {t("sendCode")}
            </Button>
          </CenterAligned>
        )}
      </FlexColumn>
      <ModalBackButton onBack={onBack} />
    </>
  )
}

type EnrollTotpProps = {
  authUrl: string
  secret: string
}

export const EnrollTotp = ({ authUrl, secret }: EnrollTotpProps) => {
  const t = useTranslations("wallet.MfaEnrollment")
  const [code, setCode] = useState("")

  const [copied, setCopied] = useState(false)
  const onCopy = async () => {
    setCopied(true)
    await navigator.clipboard.writeText(secret)
    setTimeout(() => setCopied(false), 2000)
  }

  const onSubmit = async (currentCode: string) => {
    setCode(currentCode)
    await MfaEnrollmentActions.submitEnrollment({ code: currentCode })
  }

  return (
    <>
      <FlexColumn className="gap-8 pt-12 pb-6">
        <TextDisplay className="leading-[28px]" size="sm">
          {t("scanQrCode")}
        </TextDisplay>
        <CenterAligned className="gap-4">
          <CenterAligned className="self-center rounded-xl bg-white p-3">
            <QRCodeSVG size={140} value={authUrl} />
          </CenterAligned>
          <UnstyledButton disabled={copied} onClick={onCopy}>
            <Flex className="items-center gap-1 text-secondary hover:text-text-secondary/80 focus:text-text-secondary/80 active:text-text-secondary/60">
              <TextBody className="text-center" color="inherit" size="sm">
                {t("cantScanCopyKey")}
              </TextBody>
              {copied ? <Check size={14} /> : <ContentCopy size={14} />}
            </Flex>
          </UnstyledButton>
        </CenterAligned>
        <CenterAligned className="gap-4">
          <AuthCode
            autoFocus
            onChange={setCode}
            onComplete={onSubmit}
            value={code}
          />
          <TextBody className="text-center" color="text-secondary" size="sm">
            {t("enterCodeFromApp")}
          </TextBody>
        </CenterAligned>
        <PasteFromClipboardButton
          className="mt-8 md:hidden"
          onPaste={onSubmit}
        />
      </FlexColumn>
      <ModalBackButton onBack={MfaEnrollmentActions.onBack} />
    </>
  )
}

export const MfaEnrollment = () => {
  const { step, submitting } = useSnapshot(MfaEnrollmentState)
  const { mfaMethods, loading } = useMfaMethods()

  useEffect(() => {
    return MfaEnrollmentActions.reset
  }, [])

  if (submitting || loading) {
    return (
      <CenterAligned className="h-[400px]">
        <Spinner />
      </CenterAligned>
    )
  }

  switch (step.id) {
    case "select-enrollment-option":
      return <SelectEnrollmentOption enrolledMfaMethods={mfaMethods} />
    case "enroll-sms":
      return <EnrollSms />
    case "enroll-totp":
      return <EnrollTotp authUrl={step.authUrl} secret={step.secret} />
  }
}
